import React, { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router';
import type { History, Transition } from 'history';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog, ConfirmContentType } from '../../../components/envago/ConfirmDialog';

type ConfirmationCallback = (confirmed: boolean) => void;
type ConfirmationHandler = (message: string, callback: ConfirmationCallback) => void;

// Module-level singleton — exactly one UnsavedChangesGuardProvider must be rendered per app lifetime.
// A second provider would overwrite this reference, and its cleanup would set it to undefined for both.
let confirmationHandler: ConfirmationHandler | undefined;

export const getUserConfirmation = (message: string, callback: ConfirmationCallback) => {
    if (confirmationHandler) {
        confirmationHandler(message, callback);
        return;
    }

    callback(window.confirm(message));
};

interface UnsavedChangesContextType {
    isBlocked: boolean;
    confirmIfBlocked: (options?: { allowNextNavigation?: boolean }) => Promise<boolean>;
    registerGuard: (id: string, message: string) => void;
    unregisterGuard: (id: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

interface PendingConfirmation {
    callback: ConfirmationCallback;
}

const fallbackTranslations = {
    title: 'Eingaben verwerfen?',
    content: 'Ihre Eingaben wurden noch nicht übermittelt. Wenn Sie diese Seite jetzt verlassen, gehen die erfassten Daten verloren.',
    confirm: 'Eingaben verwerfen',
    cancel: 'Weiter bearbeiten',
};

const translateWithFallback = (t: (key: string, options?: any) => string, key: string, fallback: string) => {
    const translated = t(key, { defaultValue: fallback });
    return translated === key ? fallback : translated;
};

export const UnsavedChangesGuardProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation();
    const [guards, setGuards] = useState<{ [id: string]: string }>({});
    const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | undefined>(undefined);
    const allowNextNavigationRef = useRef(false);

    const isBlocked = Object.keys(guards).length > 0;

    const requestConfirmation = useCallback((callback: ConfirmationCallback) => {
        setPendingConfirmation({ callback });
    }, []);

    useEffect(() => {
        confirmationHandler = (_message, callback) => {
            if (allowNextNavigationRef.current) {
                allowNextNavigationRef.current = false;
                callback(true);
                return;
            }

            requestConfirmation(callback);
        };

        return () => {
            confirmationHandler = undefined;
        };
    }, [requestConfirmation]);

    const registerGuard = useCallback((id: string, message: string) => {
        setGuards((currentGuards) => ({
            ...currentGuards,
            [id]: message,
        }));
    }, []);

    const unregisterGuard = useCallback((id: string) => {
        setGuards((currentGuards) => {
            const nextGuards = { ...currentGuards };
            delete nextGuards[id];
            return nextGuards;
        });
    }, []);

    const confirmIfBlocked = useCallback((options?: { allowNextNavigation?: boolean }) => {
        if (!isBlocked) {
            return Promise.resolve(true);
        }

        return new Promise<boolean>((resolve) => {
            requestConfirmation((confirmed) => {
                if (confirmed && options?.allowNextNavigation) {
                    allowNextNavigationRef.current = true;
                }
                resolve(confirmed);
            });
        });
    }, [isBlocked, requestConfirmation]);

    const contextValue = useMemo(
        () => ({
            isBlocked,
            confirmIfBlocked,
            registerGuard,
            unregisterGuard,
        }),
        [confirmIfBlocked, isBlocked, registerGuard, unregisterGuard],
    );

    const resolvePendingConfirmation = (confirmed: boolean) => {
        pendingConfirmation?.callback(confirmed);
        setPendingConfirmation(undefined);
    };

    return (
        <UnsavedChangesContext.Provider value={contextValue}>
            {children}
            <ConfirmDialog
                open={!!pendingConfirmation}
                type={ConfirmContentType.WARNING}
                showIcon={true}
                title={translateWithFallback(t, 'jobs.details.unsavedChanges.title', fallbackTranslations.title)}
                content={translateWithFallback(t, 'jobs.details.unsavedChanges.content', fallbackTranslations.content)}
                okText={translateWithFallback(t, 'jobs.details.unsavedChanges.confirm', fallbackTranslations.confirm)}
                cancelText={translateWithFallback(t, 'jobs.details.unsavedChanges.cancel', fallbackTranslations.cancel)}
                onOk={() => resolvePendingConfirmation(true)}
                onCancel={() => resolvePendingConfirmation(false)}
            />
        </UnsavedChangesContext.Provider>
    );
};

export const useUnsavedChanges = () => {
    const context = useContext(UnsavedChangesContext);

    if (!context) {
        throw new Error('useUnsavedChanges must be used inside UnsavedChangesGuardProvider');
    }

    return context;
};

export const useUnsavedChangesGuard = (when: boolean, message = 'unsaved changes') => {
    // The app renders a HistoryRouter, so the navigator is the `history` (v5) object, which can block transitions.
    const history = useContext(UNSAFE_NavigationContext).navigator as unknown as History;
    const { registerGuard, unregisterGuard } = useUnsavedChanges();
    const guardIdRef = useRef(`unsaved-changes-${Math.random().toString(36).slice(2)}`);

    // Layout effect: the blocker must be active as soon as the dirty state is committed. A passive effect runs
    // only after the next paint, so a quick back/navigation right after typing could slip through.
    useLayoutEffect(() => {
        const guardId = guardIdRef.current;

        if (!when) {
            unregisterGuard(guardId);
            return;
        }

        registerGuard(guardId, message);

        // Like history v4's block(message): every navigation asks for confirmation. A confirmed navigation passes
        // once; afterwards the guard blocks again for as long as it is active (the component may stay mounted).
        let active = true;
        let unblock = () => {};
        const block = () => {
            unblock = history.block((transition: Transition) => {
                getUserConfirmation(message, (confirmed) => {
                    if (!confirmed) {
                        return;
                    }

                    unblock();
                    const unlisten = history.listen(() => {
                        unlisten();
                        if (active) {
                            block();
                        }
                    });
                    transition.retry();
                });
            });
        };
        block();

        return () => {
            active = false;
            unblock();
            unregisterGuard(guardId);
        };
    }, [history, message, registerGuard, unregisterGuard, when]);

    useEffect(() => {
        if (!when) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [when]);
};
